import React from "react";
import {
  ArrowUpRight,
  Award,
  BadgeCheck,
  BarChart3,
  Brain,
  GraduationCap,
  ShieldCheck,
  Target,
  TrendingUp,
} from "lucide-react";
import "./TpcTrainerSpotlight.css";

import conradImage from "../../images/conrad02.png";

const trainerStats = [
  {
    id: "experience",
    value: "18+",
    label: "Years leadership & trader-development experience",
    trend: "gold",
    icon: Award,
  },
  {
    id: "trading",
    value: "Funded",
    label: "Personal and prop-firm account experience",
    trend: "green",
    icon: TrendingUp,
  },
  {
    id: "analysis",
    value: "83%",
    label: "Technical Analysis diploma average",
    trend: "red",
    icon: BarChart3,
  },
  {
    id: "coaching",
    value: "Coach",
    label: "Trading, performance and execution discipline",
    trend: "green",
    icon: Brain,
  },
];

const trainerCredentials = [
  {
    id: "lat",
    label: "Applied Financial Trading",
    value: "London Academy of Trading · Distinction",
    trend: "gold",
  },
  {
    id: "sta",
    label: "Technical Analysis",
    value: "Society of Technical Analysts · 83% average",
    trend: "green",
  },
  {
    id: "coaching",
    label: "Professional Coaching",
    value: "Executive coaching studies + ICF PCC examination",
    trend: "red",
  },
];

const trainerFocus = [
  "Price action and market structure",
  "Trade planning and execution discipline",
  "Risk process and review systems",
  "Funded-account challenge readiness",
];

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "gold",
  "red",
  "green",
  "green",
  "red",
  "green",
];

function TpcTrainerSpotlight({
  applyHref = "https://andus.co.za/trade-register",
  bootcampHref = "#tpc-price-action-trader-bootcamp-details",
}) {
  return (
    <section
      id="tpc-trainer-spotlight"
      className="trpc-trainerSpotlight"
      aria-labelledby="trpc-trainerSpotlight-title"
    >
      <div className="trpc-trainerSpotlight__marketLayer" aria-hidden="true">
        <span className="trpc-trainerSpotlight__priceLine trpc-trainerSpotlight__priceLine--one" />
        <span className="trpc-trainerSpotlight__priceLine trpc-trainerSpotlight__priceLine--two" />

        <div className="trpc-trainerSpotlight__candleGroup">
          {marketCandles.map((trend, index) => (
            <span
              key={`${trend}-${index}`}
              className="trpc-trainerSpotlight__candle"
              data-trend={trend}
            />
          ))}
        </div>
      </div>

      <div className="trpc-trainerSpotlight__shell">
        <div className="trpc-trainerSpotlight__header">
          <div className="trpc-trainerSpotlight__terminalTop" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>

          <span className="trpc-trainerSpotlight__kicker">
            Get to know our trainers
          </span>

          <h2 id="trpc-trainerSpotlight-title" className="trpc-trainerSpotlight__title">
            Learn trading from the best.
          </h2>

          <p className="trpc-trainerSpotlight__intro">
            TPC trainers bring real market experience, coaching discipline and
            structured trader-development systems into every learning session.
          </p>
        </div>

        <div className="trpc-trainerSpotlight__grid">
          <div
            className="trpc-trainerSpotlight__visual"
            aria-label="Conrad Janse van Rensburg"
          >
            <div className="trpc-trainerSpotlight__portraitStage">
              <div
                className="trpc-trainerSpotlight__portraitTerminalTop"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </div>

              <div className="trpc-trainerSpotlight__portraitMarket" aria-hidden="true">
                <span>Lead Coach</span>
                <strong>TPC</strong>
              </div>

              <div className="trpc-trainerSpotlight__portraitChart" aria-hidden="true">
                {marketCandles.slice(0, 8).map((trend, index) => (
                  <span key={`${trend}-portrait-${index}`} data-trend={trend} />
                ))}
              </div>

              <img
                className="trpc-trainerSpotlight__portrait"
                src={conradImage}
                alt="Conrad Janse van Rensburg"
                loading="lazy"
                decoding="async"
              />

              <div className="trpc-trainerSpotlight__visualBadge trpc-trainerSpotlight__visualBadge--top">
                <span>Founder & CEO</span>
                <strong>Trade Performance Centre</strong>
              </div>

              <div className="trpc-trainerSpotlight__visualBadge trpc-trainerSpotlight__visualBadge--bottom">
                <span>Trading Coach</span>
                <strong>Price Action Programme</strong>
              </div>
            </div>
          </div>

          <div className="trpc-trainerSpotlight__copy">
            <span className="trpc-trainerSpotlight__eyebrow">
              Lead Trading Coach
            </span>

            <div className="trpc-trainerSpotlight__nameBlock">
              <h3>Conrad Janse van Rensburg</h3>
              <p>Founder & CEO · TPC Trading Programme Lead Coach</p>
            </div>

            <div className="trpc-trainerSpotlight__bio">
              <p>
                Conrad coaches traders to turn price action into a repeatable
                performance process. As Founder and CEO of Trade Performance
                Centre, he brings formal trading qualifications, technical-analysis
                depth and professional coaching discipline into every session.
              </p>

              <p>
                His focus is practical: read market structure, build a trading
                plan, manage risk, review execution and develop the habits required
                for funded-account readiness. Conrad’s own trading experience
                includes personal and funded accounts, giving learners guidance
                rooted in live-market decision-making — not theory alone.
              </p>
            </div>

            <div
              className="trpc-trainerSpotlight__stats"
              aria-label="Conrad trading coach highlights"
            >
              {trainerStats.map((item) => {
                const Icon = item.icon;

                return (
                  <article
                    key={item.id}
                    className="trpc-trainerSpotlight__stat"
                    data-trend={item.trend}
                  >
                    <span className="trpc-trainerSpotlight__statIcon" aria-hidden="true">
                      <Icon size={19} strokeWidth={2.35} />
                    </span>

                    <div>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="trpc-trainerSpotlight__lowerGrid">
              <div className="trpc-trainerSpotlight__focusPanel">
                <div className="trpc-trainerSpotlight__panelHead">
                  <span className="trpc-trainerSpotlight__panelIcon" aria-hidden="true">
                    <Target size={18} strokeWidth={2.35} />
                  </span>
                  <h4>Coaching focus</h4>
                </div>

                <ul>
                  {trainerFocus.map((item) => (
                    <li key={item}>
                      <span aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="trpc-trainerSpotlight__credentialPanel">
                <div className="trpc-trainerSpotlight__panelHead">
                  <span className="trpc-trainerSpotlight__panelIcon" aria-hidden="true">
                    <GraduationCap size={18} strokeWidth={2.35} />
                  </span>
                  <h4>Credentials</h4>
                </div>

                <div className="trpc-trainerSpotlight__credentialList">
                  {trainerCredentials.map((item) => (
                    <article
                      key={item.id}
                      className="trpc-trainerSpotlight__credential"
                      data-trend={item.trend}
                    >
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div className="trpc-trainerSpotlight__trustStrip">
              <span className="trpc-trainerSpotlight__trustIcon" aria-hidden="true">
                <ShieldCheck size={18} strokeWidth={2.35} />
              </span>

              <p>
                Built for learners who want more than content — a structured
                pathway, trading discipline and a coach-led performance process.
              </p>
            </div>

            <div className="trpc-trainerSpotlight__actions">
              <a
                className="trpc-trainerSpotlight__btn trpc-trainerSpotlight__btn--primary"
                href={applyHref}
                target="_blank"
                rel="noopener noreferrer"
                data-candle="green"
              >
                <span>Train with Conrad</span>
                <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
              </a>

              <a
                className="trpc-trainerSpotlight__btn trpc-trainerSpotlight__btn--secondary"
                href={bootcampHref}
                data-candle="red"
              >
                <span>View Bootcamp</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TpcTrainerSpotlight;
